import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchFuelPrices } from "../services/apiService";
import "../styles/pages/main_page.css";

import Header from "../components/Header.jsx";
import Footer from "../components/Footer.jsx";
import Slider from "../components/Slider.jsx";
import ShortNewsCard from "../components/ShortNewsCard";
import GsmCard from "../components/GsmCard.jsx";
import AdvantageCard from "../components/AdvantageCard.jsx";
import Button from "../components/Button.jsx";

import FuelIcon from "../assets/icons/advantages_icons/fuel_icon.svg";
import NetworkIcon from "../assets/icons/advantages_icons/network_icon.svg";
import GiftIcon from "../assets/icons/advantages_icons/gift_icon.svg";
import GrowthIcon from "../assets/icons/advantages_icons/growth_icon.svg";
import ServiceIcon from "../assets/icons/advantages_icons/service_icon.svg";

import CheckIcon from "../assets/icons/virtual_card_icons/check-icon.svg";
import AppStoreIcon from "../assets/icons/links_icons/appstore_btn_icon.svg";
import GooglePlayIcon from "../assets/icons/links_icons/googleplay_btn_icon.svg";

import MapIcon from "../assets/icons/map_icon.svg";
import InfoIcon from "../assets/icons/info-icon-white.svg";
import CreditCardIcon from "../assets/icons/credit-card-icon.svg";

import PhonesImageSm from "../assets/images/mobile_app_sm.svg";
import PhonesImageLg from "../assets/images/mobile_app_lg.svg";
import StationsMapImage from "../assets/images/stations_map.png";
import FuelCardAndCouponsImageSm from "../assets/images/fuel_card_and_coupons_sm.png";

import useAutoScroll from "../hooks/useAutoScroll.js";

import {
  fetchPromotions as getPromotionsAPI,
  fetchShortNews,
  fetchDetailedNews,
} from "../services/apiService.js";

import { IMAGE_BASE_URL } from "../services/imageConfig.js";

const fuelData = [
  { name: "АИ-100", colorClass: "red" },
  { name: "АИ-95", colorClass: "orange" },
  { name: "B-POWER", colorClass: "bpower-red" },
  { name: "АИ-92", colorClass: "yellow" },
  { name: "ДТ", colorClass: "black" },
  { name: "СУГ", colorClass: "blue" },
];

const advantages = [
  {
    icon: <img src={FuelIcon} alt="Fuel Icon" />,
    title: "Качественное топливо",
    description: "Заботимся о вашей машине, и она никогда вас не подведет!",
  },
  {
    icon: <img src={NetworkIcon} alt="Network Icon" />,
    title: "Крупная сеть АЗС",
    description: "Создаем удобную и современную сеть АЗС для каждого водителя.",
  },
  {
    icon: <img src={GiftIcon} alt="Gift Icon" />,
    title: "Выгодно и экономично",
    description: "Выгодные покупки и скидки постоянным клиентам.",
  },
  {
    icon: <img src={GrowthIcon} alt="Growth Icon" />,
    title: "Карьерный рост",
    description: "Всегда рады сотрудничеству с профессионалами.",
  },
  {
    icon: <img src={ServiceIcon} alt="Service Icon" />,
    title: "Отличный сервис",
    description: "Мы стремимся к высокому уровню обслуживания.",
  },
];

const MainPage = () => {
  const navigate = useNavigate();

  const shortNewsAutoScroll = useAutoScroll({ speed: 2 });
  const advantagesAutoScroll = useAutoScroll({ speed: 2 });

  const {
    data: promotionsData,
  } = useQuery({ queryKey: ["promotions"], queryFn: getPromotionsAPI });

  const {
    data: shortNewsData,
  } = useQuery({ queryKey: ["shortNews"], queryFn: fetchShortNews });

  const {
    data: detailedNewsData,
  } = useQuery({ queryKey: ["detailedNews"], queryFn: fetchDetailedNews });


  const {
    data: fuelPricesData,
  } = useQuery({ queryKey: ["fuelPrices"], queryFn: fetchFuelPrices });

  const promotions = promotionsData
    ? promotionsData.map((item) => ({
      id: item.id,
      url: IMAGE_BASE_URL + item.titleImage,
      date: item.date,
      title: item.title,
      description: item.description,
    }))
    : [];

  const shortNews = shortNewsData
    ? shortNewsData
      .map((item) => ({
        id: item.id,
        image: IMAGE_BASE_URL + item.image,
        date: item.date,
        title: item.title,
      }))
      .slice(0, 4)
    : [];

  const detailedNews = detailedNewsData
    ? detailedNewsData.map((item) => ({
      id: item.id,
      url: IMAGE_BASE_URL + item.titleImage,
      date: item.date,
      title: item.title,
      description: item.description,
    }))
    : [];

  const mapFuelPrices = (data) => {
    if (!data) return {};
    const pricesByName = {};
    data.forEach((item) => {
      pricesByName[item.productName] = item.salePrice;
    });
    return pricesByName;
  };

  const pricesData = mapFuelPrices(fuelPricesData);

  return (
    <div className="main-page">
      <Header />
      <div className="main-page-content">
        <div className="slider-section first-slider-section">
          {promotions.length > 0 ? (
            <Slider imagesData={promotions} styleType="small" contentType="promotion" />
          ) : null}
        </div>
        <div className="short-news-section">
          <h2 className="section-title">Краткие новости</h2>
          <div
            className="short-news-container"
            ref={shortNewsAutoScroll.containerRef}
            onMouseEnter={shortNewsAutoScroll.handleMouseEnter}
            onMouseLeave={shortNewsAutoScroll.handleMouseLeave}
            onTouchStart={shortNewsAutoScroll.handleTouchStart}
            onTouchEnd={shortNewsAutoScroll.handleTouchEnd}
          >
            {shortNews.map((news) => (
              <ShortNewsCard
                key={news.id}
                image={news.image}
                date={news.date}
                title={news.title}
              />
            ))}
          </div>
        </div>
        <div className="gsm-prices-section">
          <h2 className="section-title">Актуальные цены на ГСМ</h2>
          <div className="gsm-cards-container">
            {fuelData.map((fuel) => (
              Object.prototype.hasOwnProperty.call(pricesData, fuel.name) && (
                <GsmCard
                  key={fuel.name}
                  name={fuel.name}
                  price={pricesData[fuel.name]}
                  colorClass={fuel.colorClass}
                />
              )
            ))}
          </div>
        </div>
        <div className="advantages-section">
          <h2 className="section-title">Сеть АЗС</h2>
          <h2 className="advantages-section-title">BISHKEK PETROLEUM</h2>
          <div
            className="advantages-container"
            ref={advantagesAutoScroll.containerRef}
            onMouseEnter={advantagesAutoScroll.handleMouseEnter}
            onMouseLeave={advantagesAutoScroll.handleMouseLeave}
            onTouchStart={advantagesAutoScroll.handleTouchStart}
            onTouchEnd={advantagesAutoScroll.handleTouchEnd}
          >
            {advantages.map((advantage, index) => (
              <AdvantageCard
                key={index}
                icon={advantage.icon}
                title={advantage.title}
                description={advantage.description}
              />
            ))}
          </div>
        </div>
        <div className="virtual-card-section">
          <div className="virtual-card-left">
            <h2 className="virtual-card-title">Мобильное приложение<br />"Bishkek Petroleum"</h2>
            <p className="virtual-card-description">
              Копите бонусы, получайте скидку и заправляйтесь с выгодой!
            </p>
            <div className="virtual-card-buttons-block">
              <a
                href="https://apps.apple.com/kg/app/bishkek-petroleum/id1584794644"
                aria-label="Download from App Store"
                className="store-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={AppStoreIcon} alt="Скачать в App Store" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.bishkek.petroleum&hl=ru&pli=1"
                aria-label="Download from Google Play"
                className="store-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={GooglePlayIcon} alt="Скачать в Google Play" />
              </a>
            </div>

          </div>
          <div className="virtual-card-center">
            <img
              src={PhonesImageLg}
              alt="Виртуальная карта (большая)"
              className="virtual-card-image desktop-image"
            />
            <img
              src={PhonesImageSm}
              alt="Виртуальная карта (малая)"
              className="virtual-card-image mobile-image"
            />
          </div>
          <div className="virtual-card-right">
            <div className="virtual-card-feature">
              <img src={CheckIcon} alt="check" className="check-icon" />
              <p className="virtual-card-feature-text">Оплачивайте с баланса мобильного приложения, и получайте скидку 0,5 сом с каждого литра</p>
            </div>
            <div className="virtual-card-feature">
              <img src={CheckIcon} alt="check" className="check-icon" />
              <p className="virtual-card-feature-text">Копите бонусы, кэшбек от 1%</p>
            </div>
            <div className="virtual-card-feature">
              <img src={CheckIcon} alt="check" className="check-icon" />
              <p className="virtual-card-feature-text">
                Участвуйте в акциях и спецпредложениях
              </p>
            </div>
            <div className="virtual-card-feature">
              <img src={CheckIcon} alt="check" className="check-icon" />
              <p className="virtual-card-feature-text">
                Узнавайте цены на топливо и адреса ближайших АЗС
              </p>
            </div>
          </div>
        </div>
        <div className="stations-section">
          <div className="stations-left-block">
            <h2 className="section-title">Сеть АЗС "BISHKEK PETROLEUM"</h2>
            <p className="stations-description">
              На сегодняшний день компания владеет обширной сетью АЗС, которая расположена в г. Бишкек, Чуйской и Иссыкульской областях. Компания ведет работу по ре-брэндингу и автоматизации автозаправочных комплексов согласно мировым стандартам качества и безопасности. <br /><br />
              Приоритетной линией развития компании сегодня является, строительство новых и реконструкция старых АЗС на основе современных технологий, и внедрение новых сервисов (ремонт транспортного средства, шиномонтаж, автомойка, банкоматы, терминалы для оплаты коммунальных услуг)
            </p>
            <div className="stations-btn">
              <Button
                text="Карта АЗС"
                icon={MapIcon}
                styleType="green"
                onClick={() => navigate("/stations")}
              />
            </div>
          </div>
          <img src={StationsMapImage} alt="Карта АЗС" className="stations-map" />
        </div>
        <div className="slider-section second-slider-section">
          <h2 className="section-title">Новости</h2>
          {detailedNews.length > 0 ? (
            <Slider imagesData={detailedNews} styleType="small" contentType="news" />
          ) : null}
        </div>
        <div className="about-company-section">
          <h1 className="about-company-title">BISHKEK PETROLEUM</h1>
          <div className="about-company-text-block">
            <p className="about-company-description">
              Надежные поставки топлива осуществляются партнерами - лидерами нефтеперерабатывающей отрасли в России, ОАО «Орскнефтеоргсинтез» и ОАО «НК Роснефть».
              Бесперебойная реализация ГСМ на АЗС Bishkek Petroleum возможна, благодаря нефтехранилищам, находящимся на территории г. Бишкек, Чуйской и Иссык-Кульской областях.
            </p>
            <p className="about-company-description">
              Огромное значение для Bishkek Petroleum имеет доверие клиентов.
              Именно поэтому Bishkek Petroleum тщательно следит за качеством топлива на своих заправочных станциях и строго соблюдает высокие стандарты обслуживания.
            </p>
          </div>
          <Button
            text="О компании"
            icon={InfoIcon}
            styleType="green"
            onClick={() => navigate("/about_company")}
          />
        </div>
        <div className="fuel-card-and-coupons-section">
          <img
            src={FuelCardAndCouponsImageSm}
            alt="Fuel Card Small"
            className="fuel-card-and-coupons-image"
          />
          <div className="fuel-card-and-coupons-block">
            <h3 className="fuel-card-and-coupons-title">Топливные карты и талоны</h3>
            <p className="fuel-card-and-coupons-description">
              ТОО «Бишкек Петролеум» предлагает компаниям-партнёрам широкий выбор услуг для взаимовыгодного и эффективного сотрудничества.<br /><br />
              Это даёт возможность партнёрам контролировать и оптимизировать расходы, а также обеспечивать точный учёт потребления топлива.
            </p>
          </div>
          <div className="fuel-card-and-coupons-btn">
            <Button
              text="Заказать топливную карту"
              icon={CreditCardIcon}
              styleType="green"
              onClick={() => window.open("https://cabinet.bpetroleum.kg/", "_blank", "noopener,noreferrer")}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MainPage;
